import {
  Add,
  ChevronDown,
  ChevronUp,
  Grid,
  Table as TableIcon,
} from '@carbon/react/icons';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import {
  Button,
  Dropdown,
  RadioButton,
  RadioButtonGroup,
  Search,
  SkeletonPlaceholder,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tile,
  Table,
} from '@carbon/react';

import { CardsGrid } from 'components/cards-grid';
import CustomToggle from 'components/custom-toggle';
import { Date as DateComponent } from 'components/date';
import DateFilter from 'components/date-filter/date-filter';
import { DevelopmentAreaWizard } from 'components/development-area-wizard';
import { EmptyState } from 'components/empty-state';
import ErrorToast from 'components/error-toast';
import { DevelopmentArea } from 'components/icons';
import { Link } from 'components/link';
import { PageHeader } from 'components/page-header';
import { SearchResultCard } from 'components/search-result-card';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import SortOrder, { SortOption } from 'components/sort-order';
import { PaginatedTable } from 'components/table/paginated-table';
import { subDays } from 'date-fns';
import { useAtom, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { DEFAULT_PAGE_SIZE, format, pageSizeAtom } from 'lib/ui';
import { debounce } from 'lodash-es';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory, useLocation } from 'react-router-dom';
import { DevelopmentAreaQueryResponse, State } from 'types/api-responses';
import { pageTitle } from 'utils/page-title';
import style from './page.module.scss';
import { statesMutationAtom, useDevelopmentAreasCountAtom } from './show/atoms';
import moment from 'moment';
import StateFilter from 'components/state-filter/state-filter';
import { SOURCE_DOCUMENT_ASSIGNMENT_DROPDOWN_ITEMS } from './constants';

const isTableViewAtom = atomWithStorage(
  'development-areas-table-preference',
  false
);

const checkForActiveFilters = (
  currentPayload: DevelopmentAreaQueryParams | undefined
): boolean => {
  if (!currentPayload) return false;

  return Boolean(
    currentPayload.created_at !== undefined ||
    currentPayload.updated_at !== undefined ||
    currentPayload.unitType !== null ||
    (currentPayload.units && currentPayload.units !== 'all') ||
    currentPayload.unit_allocation_flag === true ||
    (currentPayload.sort_by && currentPayload.sort_by !== 'created_at') ||
    (currentPayload.sort_order && currentPayload.sort_order !== 'desc')
  );
};

const DevelopmentAreasPage = () => {
  const [isTable, setIsTable] = useAtom(isTableViewAtom);
  const [isOpen, toggleIsOpen] = useReducer((s) => !s, false);
  const [pageSizePreference] = useAtom(pageSizeAtom);
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchAtoms = useMemo(() => createSearchAtom(pageSizePreference), []);
  const [searchAtomValue, setSearchPayload] = useAtom(searchAtoms.searchAtom);
  const countAtom = useDevelopmentAreasCountAtom();
  const [countData] = useAtom(countAtom);
  const dateFilterRef = useRef<{
    setFilterOption: (option: string) => void;
    setStartDate: (date: string) => void;
    setEndDate: (date: string) => void;
  }>(null);

  const [statesQuery, fetchStates] = useAtom(statesMutationAtom);
  const [statesError, setStatesError] = useState<string | null>(null);

  const fetchStatesData = useCallback(() => {
    fetchStates({
      type: 'POST',
      url: '/queries/states',
      data: { hasDevelopmentAreas: true },
      onError: (error) => {
        setStatesError(
          (error as { message: string })?.message ||
            'Unable to display the states.'
        );
      },
    });
  }, [fetchStates]);

  useEffect(() => {
    fetchStatesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefetchStates = () => {
    setStatesError(null);
    fetchStatesData();
  };

  const states =
    statesQuery.result?.type === 'success'
      ? statesQuery.result.data?.results || []
      : [];

  const getCount = () => {
    if (!countData.data || 'error' in countData.data) return 0;
    return countData.data.count;
  };

  const get24HCount = () => {
    if (!countData.data || 'error' in countData.data) {
      return 0;
    }
    return countData.data.count24h;
  };

  const get7DCount = () => {
    if (!countData.data || 'error' in countData.data) {
      return 0;
    }
    return countData.data.count7d;
  };
  const history = useHistory();

  const handleDateFilter = (daysAgo: number) => {
    // Ensure filters are visible
    setIsDateFilterOpen(true);

    const endDate = new Date();
    const startDate = subDays(endDate, daysAgo);

    // Format dates
    const formattedStartDate = formatDateToYYYYMMDD(startDate);
    const formattedEndDate = formatDateToYYYYMMDD(endDate);

    // Check if clicking the same stat again
    const currentCreatedAt = searchAtomValue.currentPayload?.created_at;
    if (
      currentCreatedAt?.start === formattedStartDate &&
      currentCreatedAt?.end === formattedEndDate
    ) {
      // Reset the date filter component state
      if (dateFilterRef.current) {
        dateFilterRef.current.setFilterOption('none');
        dateFilterRef.current.setStartDate('');
        dateFilterRef.current.setEndDate('');
      }

      // Clear the search payload
      const params = new URLSearchParams(location.search);
      params.delete('dateFilterType');
      params.delete('created_start');
      params.delete('created_end');
      params.set('page', '1');
      history.push({ pathname: location.pathname, search: params.toString() });
    }

    // Update the date filter component state
    if (dateFilterRef.current) {
      dateFilterRef.current.setFilterOption('created');
      dateFilterRef.current.setStartDate(formattedStartDate);
      dateFilterRef.current.setEndDate(formattedEndDate);
    }

    // Update URL parameters
    const params = new URLSearchParams(location.search);
    params.set('dateFilterType', 'created');
    params.set('created_start', formattedStartDate);
    params.set('created_end', formattedEndDate);
    params.set('page', '1');
    history.push({ pathname: location.pathname, search: params.toString() });
  };

  const formatDateToYYYYMMDD = (date: Date) => {
    return moment(date).format('YYYY-MM-DD');
  };

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return checkForActiveFilters(searchAtomValue.currentPayload);
  }, [searchAtomValue.currentPayload]);

  // Keep filters open if there are active filters
  useEffect(() => {
    if (hasActiveFilters) {
      setIsDateFilterOpen(true);
    }
  }, [hasActiveFilters]);

  return (
    <>
      <Helmet>
        <title>{pageTitle('Development Areas')}</title>
      </Helmet>
      <DevelopmentAreaWizard
        developmentArea={undefined}
        isOpen={isOpen}
        onClose={() => toggleIsOpen()}
        onCreated={() => {
          setSearchPayload((current) => ({
            ...current,
            text: '',
            page: 1,
            unitType: null,
          }));
          toggleIsOpen();
        }}
      />
      <div className={style.developmentAreasPageHeader}>
        <PageHeader
          title="Development Areas"
          breadcrumbs={[['development_areas', 'Development Areas']]}
          aside={
            <div className={style.statsArea}>
              <div className={style.statBox}>
                <div className={style.statLabel}>Total Dev Areas</div>
                <div className={style.statValue}>
                  {countData.loading ? (
                    <SkeletonPlaceholder
                      className={style.placeholderSkeletonItem}
                    />
                  ) : (
                    format.number(getCount())
                  )}
                </div>
              </div>
              <div
                className={style.statBox}
                onClick={() => handleDateFilter(1)}
                title="Click to filter by last 24 hours"
                style={{ cursor: 'pointer' }}
              >
                <div className={style.statLabel}>Last 24 Hours</div>
                <div className={style.changeValue}>
                  {countData.loading ? (
                    <SkeletonPlaceholder
                      className={style.placeholderSkeletonItem}
                    />
                  ) : (
                    `+${format.number(get24HCount())}`
                  )}
                </div>
              </div>
              <div
                className={style.statBox}
                onClick={() => handleDateFilter(7)}
                title="Click to filter by last 7 days"
                style={{ cursor: 'pointer' }}
              >
                <div className={style.statLabel}>Last 7 Days</div>
                <div className={style.changeValue}>
                  {countData.loading ? (
                    <SkeletonPlaceholder
                      className={style.placeholderSkeletonItem}
                    />
                  ) : (
                    `+${format.number(get7DCount())}`
                  )}
                </div>
              </div>
            </div>
          }
          actions={
            <div className={style.sortView}>
              <Button size="sm" renderIcon={Add} onClick={() => toggleIsOpen()}>
                <span>Create Development Area</span>
              </Button>
              <Button
                hasIconOnly
                size="md"
                kind={isTable ? 'secondary' : 'ghost'}
                iconDescription="Table View"
                renderIcon={TableIcon}
                onClick={() => setIsTable(true)}
              />
              <Button
                hasIconOnly
                size="md"
                kind={!isTable ? 'secondary' : 'ghost'}
                iconDescription="Grid View"
                renderIcon={Grid}
                onClick={() => setIsTable(false)}
              />
            </div>
          }
        />
      </div>
      <div className={style.mainContent}>
        <div className={style.filtersArea}>
          <DevAreasTypeFilter
            searchAtoms={searchAtoms}
            isDateFilterOpen={isDateFilterOpen}
            setIsDateFilterOpen={setIsDateFilterOpen}
            dateFilterRef={dateFilterRef}
            states={states}
            statesLoading={statesQuery.loading}
            statesError={statesError}
            onRefetchStates={onRefetchStates}
          />
        </div>
        {isTable ? (
          <DevelopmentAreasTable searchAtoms={searchAtoms} />
        ) : (
          <DevelopmentAreasList searchAtoms={searchAtoms} />
        )}
      </div>
    </>
  );
};

const DevAreasTypeFilter = ({
  searchAtoms,
  isDateFilterOpen,
  setIsDateFilterOpen,
  states,
  statesLoading,
  statesError,
  onRefetchStates,
}: {
  searchAtoms: ReturnType<typeof createSearchAtom>;
  isDateFilterOpen: boolean;
  setIsDateFilterOpen: (isOpen: boolean) => void;
  dateFilterRef: React.MutableRefObject<{
    setFilterOption: (option: string) => void;
    setStartDate: (date: string) => void;
    setEndDate: (date: string) => void;
  } | null>;
  states: State[];
  statesLoading: boolean;
  statesError: string | null;
  onRefetchStates: () => void;
}) => {
  const { searchAtom } = searchAtoms;
  const [search, setSearchPayload] = useAtom(searchAtom);
  const [pageSizePreference] = useAtom(pageSizeAtom);
  const unitTypeParam = search.currentPayload?.unitType;
  const history = useHistory();
  const location = useLocation();
  const [searchDisplayText, setSearchDisplayText] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [showDevAreasWithUnits, setShowDevAreasWithUnits] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('unit_allocation_flag') === 'true';
  });

  const [sourceDocumentAssignment, setSourceDocumentAssignment] = useState<
    null | string
  >(() => {
    const params = new URLSearchParams(location.search);
    return params.get('source_document_assignment') || null;
  });

  const handleSourceDocumentChange = (data: {
    selectedItem: { id: string; text: string } | null;
  }) => {
    if (!showDevAreasWithUnits) return;
    if (!data.selectedItem) return;
    setSourceDocumentAssignment(data.selectedItem.id);

    // Update URL parameters
    const params = new URLSearchParams(location.search);
    if (data.selectedItem) {
      params.set('source_document_assignment', data.selectedItem.id);
    }
    params.set('page', '1');
    history.push({ search: params.toString() });

    // Update search payload
    setSearchPayload((current) => {
      if (!current) return undefined;
      if (!data.selectedItem) return current;
      const updated: DevelopmentAreaQueryParams = {
        ...current,
        source_document_assignment: data.selectedItem.id,
        page: 1,
      };
      return updated;
    });
  };

  const debouncedSetSearchPayload = useMemo(
    () =>
      debounce((value: string) => {
        const params = new URLSearchParams(location.search);
        if (value) {
          params.set('search', value);
        } else {
          params.delete('search');
        }
        params.set('page', '1');
        history.push({ search: params.toString() });

        setSearchPayload((current) => ({
          ...current,
          text: value,
          page: 1,
          unitType: current?.unitType ?? null,
        }));
      }, 400),
    [location.search, history, setSearchPayload]
  );

  const selected =
    unitTypeParam === null ? -1 : unitTypeParam === 'horizontal' ? 0 : 1;

  const setUnitType = useCallback(
    (unitType: string | null) => {
      const params = new URLSearchParams(location.search);
      if (unitType) {
        params.set('unitType', unitType);
      } else {
        params.delete('unitType');
      }
      params.set('page', '1');
      history.push({ search: params.toString() });

      setSearchPayload((current) => ({
        ...current,
        unitType,
        page: 1,
        pageSize: pageSizePreference,
      }));
    },
    [location.search, history, setSearchPayload, pageSizePreference]
  );

  return (
    <>
      <div className={style.actionsArea}>
        <div className={style.searchAndFilters}>
          <div className={style.devSearchField}>
            {search?.error ? <ErrorToast message={search?.error} /> : null}
            <Search
              labelText="Search"
              placeholder="Search"
              size="lg"
              {...{ autoFocus: true }}
              value={searchDisplayText}
              onChange={({ target: { value } }) => {
                setSearchDisplayText(value);
                debouncedSetSearchPayload(value);
              }}
            />
            <div className={style.dropdownButton}>
              <Button
                data-testid="search-expand-button"
                kind="ghost"
                hasIconOnly
                iconDescription={
                  isDateFilterOpen ? 'Collapse Filters' : 'Expand Filters'
                }
                size="md"
                onClick={() => setIsDateFilterOpen(!isDateFilterOpen)}
                renderIcon={isDateFilterOpen ? ChevronUp : ChevronDown}
                tooltipAlignment="center"
                tooltipPosition="bottom"
              />
            </div>
          </div>
          {isDateFilterOpen && (
            <div className={style.filterControls}>
              <div className={style.dateFilterWrapper}>
                {/* Pass setSearchPayload and searchAtom to DateFilter */}
                <DateFilter setSearchPayload={setSearchPayload} />
              </div>
              <div className={style.sortOrderWrapper}>
                <SortOrder
                  value={`${
                    search.currentPayload?.sort_by === 'updated_at'
                      ? 'updated'
                      : 'created'
                  }-${
                    search.currentPayload?.sort_order === 'asc'
                      ? 'oldest'
                      : 'newest'
                  }`}
                  onChange={(option: SortOption) => {
                    const params = new URLSearchParams(location.search);
                    params.set('sortOrder', option.id); // Keep for backward compatibility
                    params.set('sort_by', option.sort_by);
                    params.set('sort_order', option.sort_order);
                    params.set('page', '1');
                    history.push({ search: params.toString() });

                    setSearchPayload((current) => {
                      if (!current) return undefined;

                      // Create new object with all existing properties
                      const updated: DevelopmentAreaQueryParams = {
                        ...current,
                        sort_by: option.sort_by,
                        sort_order: option.sort_order,
                        page: 1,
                      };

                      return updated;
                    });
                  }}
                />
                <StateFilter
                  selectedStates={search.currentPayload?.states || []}
                  onChange={(states) => {
                    // Update URL params
                    const params = new URLSearchParams(location.search);
                    if (states.length) {
                      params.set('states', states.join(','));
                    } else {
                      params.delete('states');
                    }
                    params.set('page', '1');
                    history.push({ search: params.toString() });
                    // Update search payload
                    setSearchPayload((current) => {
                      if (!current) return undefined;
                      return {
                        ...current,
                        states: states,
                        page: 1,
                      };
                    });
                  }}
                  states={states}
                  loading={statesLoading}
                  error={statesError}
                  onRefetch={onRefetchStates}
                />
              </div>
              <div className={style.unitsWrapper}>
                <h1 className={style.heading}>Units</h1>
                <RadioButtonGroup
                  name="units"
                  orientation="vertical"
                  valueSelected={search.currentPayload?.units || 'all'}
                  onChange={(value) => {
                    // Only proceed if we have a valid string value
                    if (typeof value !== 'string') return;

                    const unitValue = value as
                      'all' | 'plat_based' | 'dev_areas';

                    // Update URL parameters
                    const params = new URLSearchParams(location.search);
                    if (unitValue === 'all') {
                      params.delete('units');
                    } else {
                      params.set('units', unitValue);
                    }
                    params.set('page', '1');
                    history.push({ search: params.toString() });

                    // Update search payload
                    setSearchPayload((current) => {
                      if (!current) return undefined;

                      const updated: DevelopmentAreaQueryParams = {
                        ...current,
                        units: unitValue,
                        page: 1,
                      };

                      return updated;
                    });
                  }}
                >
                  <RadioButton id="all-units" labelText="All" value="all" />
                  <RadioButton
                    id="plat-based-units"
                    labelText="Plat Based Units"
                    value="plat_based"
                  />
                  <RadioButton
                    id="dev-areas"
                    labelText="Dev Areas"
                    value="dev_areas"
                  />
                </RadioButtonGroup>
              </div>
              <div className={style.unitTypeWrapper}>
                <h1 className={style.heading}>Unit Type</h1>
                <RadioButtonGroup
                  name="unit-type"
                  orientation="vertical"
                  valueSelected={selected}
                  onChange={(value) => {
                    if (typeof value !== 'number') return;
                    if (value < 0) {
                      setUnitType(null);
                      return;
                    }
                    setUnitType(value === 0 ? 'horizontal' : 'vertical');
                  }}
                >
                  <RadioButton value={-1} labelText="All" />
                  <RadioButton value={0} labelText="Horizontal" />
                  <RadioButton value={1} labelText="Vertical" />
                </RadioButtonGroup>
              </div>
              <div className={style.toggleWrapper}>
                <h1 className={style.heading}>
                  Show Dev Areas with Unit Allocations
                </h1>
                <div className={style.toggleContent}>
                  <CustomToggle
                    data-testid="show-dev-areas-toggle"
                    id="show-dev-areas-toggle"
                    size="sm"
                    checked={showDevAreasWithUnits}
                    onChange={(checked: boolean) => {
                      setShowDevAreasWithUnits(checked);

                      // Update URL parameters
                      const params = new URLSearchParams(location.search);
                      if (checked) {
                        params.set('unit_allocation_flag', 'true');
                      } else {
                        params.delete('unit_allocation_flag');
                        params.delete('source_document_assignment');
                        setSourceDocumentAssignment(null);
                      }
                      params.set('page', '1');
                      history.push({ search: params.toString() });

                      // Update search payload
                      setSearchPayload((current) => {
                        if (!current) return undefined;

                        const updated: DevelopmentAreaQueryParams = {
                          ...current,
                          unit_allocation_flag: checked,
                          page: 1,
                        };

                        delete updated.source_document_assignment;
                        return updated;
                      });
                    }}
                  />
                </div>
                {showDevAreasWithUnits && (
                  <div className={style.assignmentDropdown}>
                    <Dropdown
                      id="source-document-assignment"
                      titleText=""
                      items={SOURCE_DOCUMENT_ASSIGNMENT_DROPDOWN_ITEMS}
                      label="Source Document Assignment"
                      selectedItem={
                        SOURCE_DOCUMENT_ASSIGNMENT_DROPDOWN_ITEMS.find(
                          (item) => item.id === sourceDocumentAssignment
                        ) || null
                      }
                      onChange={handleSourceDocumentChange}
                      itemToString={(item) => (item ? item.text : '')}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
type DateRange = {
  start?: string;
  end?: string;
};

type DevelopmentAreaQueryParams = {
  unitType: string | null;
  sort_by?: 'created_at' | 'updated_at';
  sort_order?: 'desc' | 'asc';
  units?: 'all' | 'plat_based' | 'dev_areas';
  unit_allocation_flag?: boolean;
  source_document_assignment?: string;
  created_at?: DateRange;
  updated_at?: DateRange;
  states?: string[];
} & SearchParams;

const createSearchAtom = (pageSizePreference: number) => {
  const location = window.location;
  const params = new URLSearchParams(location.search);
  const dateFilterType = params.get('dateFilterType');

  // Get units parameter from URL or use default
  const units =
    (params.get('units') as 'all' | 'plat_based' | 'dev_areas') || 'all';

  // Get unit allocation flag from URL
  const unitAllocationFlag = params.get('unit_allocation_flag') === 'true';

  // Get source document assignment from URL
  const sourceDocumentAssignment =
    params.get('source_document_assignment') || 'assigned';

  // Initialize date filters
  let createdAt: DateRange | undefined;
  let updatedAt: DateRange | undefined;

  if (dateFilterType === 'created') {
    const startDate = params.get('created_start');
    const endDate = params.get('created_end');

    if (startDate || endDate) {
      createdAt = {
        start: startDate || undefined,
        end: endDate || undefined,
      };
    }
  } else if (dateFilterType === 'updated') {
    const startDate = params.get('updated_start');
    const endDate = params.get('updated_end');

    if (startDate || endDate) {
      updatedAt = {
        start: startDate || undefined,
        end: endDate || undefined,
      };
    }
  }

  // Handle sort parameters, including backward compatibility with sortOrder
  const sortOrder = params.get('sortOrder');
  let sortBy: 'created_at' | 'updated_at';
  let sortingOrder: 'desc' | 'asc';

  if (sortOrder) {
    // Parse from the combined format (e.g., 'created-newest')
    const [field, order] = sortOrder.split('-');
    sortBy = field === 'updated' ? 'updated_at' : 'created_at';
    sortingOrder = order === 'oldest' ? 'asc' : 'desc';
  } else {
    // Use individual parameters if available
    sortBy =
      (params.get('sort_by') as 'created_at' | 'updated_at') || 'created_at';
    sortingOrder = (params.get('sort_order') as 'desc' | 'asc') || 'desc';
  }

  // Parse states from URL
  const statesParam = params.get('states');
  const states = statesParam ? statesParam.split(',') : [];

  return searchAtomPair<
    DevelopmentAreaQueryResponse,
    DevelopmentAreaQueryParams
  >('developmentAreas', {
    text: params.get('search') || '',
    page: parseInt(params.get('page') || '1', 10),
    pageSize: pageSizePreference,
    unitType: params.get('unitType'),
    sort_by: sortBy,
    sort_order: sortingOrder,
    units: units,
    unit_allocation_flag: unitAllocationFlag,
    source_document_assignment: sourceDocumentAssignment,
    created_at: createdAt,
    updated_at: updatedAt,
    states,
  });
};

function toTitleCase(str: string | null): string | null {
  // eslint-disable-next-line eqeqeq
  return str == null
    ? null
    : str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

const DevelopmentAreasList = ({
  searchAtoms: { searchAtom },
}: {
  searchAtoms: ReturnType<typeof createSearchAtom>;
}) => {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const setPageSize = useSetAtom(pageSizeAtom);

  return (
    <>
      <CardsGrid<DevelopmentAreaQueryResponse>
        searchResponse={searchResult.data}
        loading={searchResult.loading}
        itemName="development area"
        onChange={(newSearch) => {
          if (newSearch.pageSize) {
            setPageSize(newSearch.pageSize);
          }
          setSearchPayload(
            (current) => current && { ...current, ...newSearch }
          );
        }}
        actions={null}
        renderCard={(developmentArea) => (
          <SearchResultCard<DevelopmentAreaQueryResponse>
            Icon={DevelopmentArea}
            item={developmentArea}
            aside={
              developmentArea.isImported && (
                <Tag type="cyan" size="sm">
                  Imported
                </Tag>
              )
            }
            cardContent={
              <>
                {developmentArea.createdBy && (
                  <dl>
                    <dt className={style.label}>Created By</dt>
                    <dd className={style.content}>
                      {developmentArea.createdBy}
                    </dd>
                  </dl>
                )}
                {developmentArea.createdAt && (
                  <dl>
                    <dt className={style.label}>Created At</dt>
                    <dd className={style.content}>
                      <DateComponent date={developmentArea.createdAt} />
                    </dd>
                  </dl>
                )}
                <dl>
                  <dt className={style.label}>Survey Area #1</dt>
                  <dd className={style.content}>
                    {developmentArea.anchorGeography?.name ??
                      'No Survey or Abstract Found'}
                  </dd>
                </dl>
                <dl>
                  <dt className={style.label}>Unit Type</dt>
                  <dd className={style.content}>
                    {toTitleCase(developmentArea.unitType)}
                  </dd>
                </dl>
                {developmentArea.unitName && (
                  <dl>
                    <dt className={style.label}>Name</dt>
                    <dd className={style.content}>
                      {developmentArea.unitName}
                    </dd>
                  </dl>
                )}
              </>
            }
          />
        )}
        renderEmptyState={
          <EmptyState
            IconComponent={DevelopmentArea}
            headerText="No development areas available"
            helperText="Currently you don't have any development areas. Create an area to start."
          />
        }
      />
    </>
  );
};

const DevelopmentAreasTable = ({
  searchAtoms: { searchAtom },
}: {
  searchAtoms: ReturnType<typeof createSearchAtom>;
}) => {
  const [searchResults, setSearchPayload] = useAtom(searchAtom);
  const searchParams = searchResults.currentPayload;
  const pageSize = searchResults?.data?.pageSize || DEFAULT_PAGE_SIZE;
  const setPageSize = useSetAtom(pageSizeAtom);

  return (
    <PaginatedTable
      pageSizes={[16, 24, 48, DEFAULT_PAGE_SIZE]}
      pageSize={pageSize}
      className={style.devAreasTable}
      page={searchParams?.page || 1}
      totalItems={searchResults?.data?.totalCount || 0}
      persistentPagination
      paginationOnTop={true}
      onPaginate={({ page, pageSize }) => {
        setPageSize(pageSize);
        setSearchPayload(
          (current) =>
            current && {
              ...current,
              page,
              pageSize,
            }
        );
      }}
      isLoading={searchResults.loading || false}
      loadingComponent={
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>State</TableHeader>
              <TableHeader>Created By</TableHeader>
              <TableHeader>Created At</TableHeader>
              <TableHeader>Date Updated</TableHeader>
              <TableHeader>Survey Area #1</TableHeader>
              <TableHeader>Unit Type</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            <SkeletonTableRows rows={pageSize} columns={7} />
          </TableBody>
        </Table>
      }
      emptyStateComponent={
        <Tile>
          <EmptyState
            IconComponent={DevelopmentArea}
            headerText="No development areas available"
            helperText="Currently you don't have any development areas. Create an area to start."
          />
        </Tile>
      }
    >
      <TableHead>
        <TableRow>
          <TableHeader>Name</TableHeader>
          <TableHeader>State</TableHeader>
          <TableHeader>Created By</TableHeader>
          <TableHeader>Created At</TableHeader>
          <TableHeader>Date Updated</TableHeader>
          <TableHeader>Survey Area #1</TableHeader>
          <TableHeader>Unit Type</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {searchResults.data?.results.map((el) => {
          return (
            <TableRow key={el.id}>
              <TableCell className={style.linkArea}>
                {el.href ? (
                  <>
                    <Link to={el.href}>
                      {el.name + (el.unitName ? ' (' + el.unitName + ')' : '')}
                    </Link>
                    {el.isImported && (
                      <Tag type="cyan" size="sm">
                        Imported
                      </Tag>
                    )}
                  </>
                ) : (
                  el.name
                )}
              </TableCell>
              <TableCell>{el.state ?? '--'}</TableCell>
              <TableCell>{el.createdBy}</TableCell>
              <TableCell>
                <DateComponent date={el.createdAt} />
              </TableCell>
              <TableCell>
                {el.updatedAt ? <DateComponent date={el.updatedAt} /> : '--'}
              </TableCell>
              <TableCell>
                {el.anchorGeography?.name ?? 'No Survey or Abstract Found'}
              </TableCell>
              <TableCell>{toTitleCase(el.unitType)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </PaginatedTable>
  );
};

export { DevelopmentAreasPage };
